import * as React from 'react';
import { useLocation } from 'react-router-dom';
import { useMutation } from '@tanstack/react-query';
import { AlertBanner, Button, Card, IconButton, useLocale, useTheme } from '@ministry/ui/internal';
import Sun from 'lucide-react/dist/esm/icons/sun.js';
import Moon from 'lucide-react/dist/esm/icons/moon.js';
import Check from 'lucide-react/dist/esm/icons/check.js';
import X from 'lucide-react/dist/esm/icons/x.js';

import { changePassword, ApiError, NetworkError } from '../lib/auth.js';
import { checkPasswordClientSide, PASSWORD_MAX_LENGTH, PASSWORD_MIN_LENGTH } from '../lib/password-policy.js';
import { useSession } from '../lib/session.js';
import { roleLabel } from '../lib/roles.js';
import { ROUTES } from '../lib/nav.js';
import { useT } from '../i18n/strings.js';
import { LocaleToggle } from '../components/LocaleToggle.js';
import { PasswordField } from '../components/PasswordField.js';
import { PageHeader } from '../components/PageHeader.js';

interface AccountSettingsLocationState {
  /** Set by the Dashboard's `mustChangePassword` banner link — see that page. */
  focusPassword?: boolean;
}

/**
 * SCREEN_SPECS.md I23. Real, working sections: identity display; language and theme
 * (same controls, same saved preference, as the top bar — §8.8); and now password
 * change, wired to `POST /auth/password/change` (§13.1). MFA enrollment and
 * notification-channel preferences remain unbuilt — no backend endpoint for either
 * exists in this build — and are still named plainly rather than left as a menu item
 * that goes nowhere.
 *
 * **Password change is written to the documented contract, and is HONEST about the one
 * gap left**: verified against a running server, `POST /auth/password/change` is not
 * actually mounted yet (`auth-routes.ts` declares six routes and this is not one of
 * them; a live request answers `404 NOT_FOUND`). This form still fully validates,
 * submits, and handles every real response shape the contract defines — the moment the
 * route lands, this closes I3/A-043's loop with no further client work — and renders
 * that specific 404 as its own honest state below, never disguised as a generic failure.
 */
export function AccountSettingsPage(): React.JSX.Element {
  const t = useT();
  const location = useLocation();
  const { data: session } = useSession();
  const { theme, setPreference } = useTheme();
  const { locale } = useLocale();

  const focusPassword = (location.state as AccountSettingsLocationState | null)?.focusPassword ?? false;
  const currentPasswordRef = React.useRef<HTMLInputElement>(null);

  const [currentPassword, setCurrentPassword] = React.useState('');
  const [newPassword, setNewPassword] = React.useState('');
  const [confirmPassword, setConfirmPassword] = React.useState('');
  const [touched, setTouched] = React.useState<{ current?: boolean; confirm?: boolean }>({});
  const [succeeded, setSucceeded] = React.useState(false);

  React.useEffect(() => {
    // Depends on `session` too, not just `focusPassword`: this component returns `<></>`
    // (nothing rendered, `currentPasswordRef` unattached) for every render before `/auth/
    // me` resolves. `focusPassword` itself never changes across that transition — a
    // dependency array of `[focusPassword]` alone would run this effect once, while the
    // ref was still null, and never again. Depending on `session` as well re-runs it the
    // moment the field this ref points at actually exists in the DOM.
    if (focusPassword) currentPasswordRef.current?.focus();
  }, [focusPassword, session]);

  const check = checkPasswordClientSide(newPassword, session?.user.username);
  const passwordsMatch = confirmPassword !== '' && confirmPassword === newPassword;
  const confirmMismatch = touched.confirm && confirmPassword !== '' && !passwordsMatch;
  const currentMissing = touched.current && currentPassword === '';
  const canSubmit = currentPassword !== '' && newPassword !== '' && check.acceptable && passwordsMatch;

  const mutation = useMutation({
    mutationFn: () => changePassword({ currentPassword, newPassword }),
    onSuccess: () => {
      setCurrentPassword('');
      setNewPassword('');
      setConfirmPassword('');
      setTouched({});
      setSucceeded(true);
    },
  });

  const serverError = mutation.error;
  const wrongCurrentPassword = serverError instanceof ApiError && serverError.code === 'AUTH_INVALID_CREDENTIALS';
  const serverPasswordRejections =
    serverError instanceof ApiError && serverError.code === 'VALIDATION_FAILED'
      ? serverError.fieldErrors.filter((fe) => fe.field === 'newPassword').map((fe) => fe.code)
      : [];
  const notYetAvailable = serverError instanceof ApiError && serverError.status === 404;

  const submit = () => {
    setTouched({ current: true, confirm: true });
    setSucceeded(false);
    if (!canSubmit) return;
    mutation.mutate();
  };

  if (!session) return <></>;

  const role = roleLabel(session.roles[0]?.roleCode, locale);

  const checklistRow = (ok: boolean, label: string) => (
    <li className={`flex items-center gap-2 text-sm ${ok ? 'text-status-good-tint-fg' : 'text-text-muted'}`}>
      {ok ? <Check aria-hidden="true" width={14} height={14} /> : <X aria-hidden="true" width={14} height={14} />}
      <span>{label}</span>
    </li>
  );

  return (
    <div className="flex flex-col gap-4">
      <PageHeader
        title={t('accountSettings.title')}
        breadcrumb={[{ label: t('shell.dashboard'), href: ROUTES.dashboard }, { label: t('accountSettings.title') }]}
      />

      <Card className="flex flex-col gap-1">
        <h2 className="text-sm font-semibold text-text-muted">{t('accountSettings.identity.title')}</h2>
        <p className="text-lg text-text-primary">{session.user.fullName}</p>
        <p className="text-sm text-text-muted">{session.user.username} · {role}</p>
      </Card>

      <Card className="flex flex-col gap-2">
        <h2 className="text-sm font-semibold text-text-muted">{t('accountSettings.language.title')}</h2>
        <p className="text-sm text-text-muted">{t('accountSettings.language.body')}</p>
        <div>
          <LocaleToggle />
        </div>
      </Card>

      <Card className="flex flex-col gap-2">
        <h2 className="text-sm font-semibold text-text-muted">{t('accountSettings.theme.title')}</h2>
        <p className="text-sm text-text-muted">{t('accountSettings.theme.body')}</p>
        <div className="flex items-center gap-2">
          <IconButton
            label={theme === 'dark' ? t('shell.theme.switchToLight') : t('shell.theme.switchToDark')}
            icon={theme === 'dark' ? <Moon width={18} height={18} /> : <Sun width={18} height={18} />}
            onClick={() => setPreference(theme === 'dark' ? 'light' : 'dark')}
          />
          <span className="text-sm text-text-primary">{theme === 'dark' ? t('shell.theme.dark') : t('shell.theme.light')}</span>
        </div>
      </Card>

      <Card className="flex flex-col gap-4">
        <div>
          <h2 className="text-sm font-semibold text-text-muted">{t('accountSettings.password.title')}</h2>
          <p className="text-sm text-text-muted">{t('accountSettings.password.body')}</p>
        </div>

        {focusPassword ? <AlertBanner variant="warning" title={t('accountSettings.password.forcedNotice')} /> : null}

        {succeeded ? (
          <AlertBanner variant="info" title={t('accountSettings.password.success')} onDismiss={() => setSucceeded(false)} />
        ) : null}

        {notYetAvailable ? (
          <AlertBanner variant="info" title={t('accountSettings.password.notAvailable.title')}>
            {t('accountSettings.password.notAvailable.body')}
          </AlertBanner>
        ) : wrongCurrentPassword ? (
          <AlertBanner variant="critical" title={t('accountSettings.password.error.wrongCurrent')} />
        ) : serverError && !notYetAvailable ? (
          <AlertBanner
            variant="critical"
            title={
              serverError instanceof NetworkError || (serverError instanceof ApiError && serverError.retryable)
                ? t('accountSettings.password.error.network')
                : t('accountSettings.password.error.generic')
            }
          />
        ) : null}

        <PasswordField
          id="account-current-password"
          ref={currentPasswordRef}
          label={t('accountSettings.password.current')}
          autoComplete="current-password"
          value={currentPassword}
          onChange={(e) => setCurrentPassword(e.target.value)}
          onBlur={() => setTouched((prev) => ({ ...prev, current: true }))}
          errorText={currentMissing ? t('fileIntake.applicant.required') : undefined}
          required
        />

        <div className="flex flex-col gap-2">
          <PasswordField
            id="account-new-password"
            label={t('accountSettings.password.new')}
            autoComplete="new-password"
            value={newPassword}
            onChange={(e) => setNewPassword(e.target.value)}
            required
          />
          {/* Live-updating checklist (I3's own deliberate exception to validate-on-blur):
              guidance while typing, never an error state of its own. */}
          <ul className="flex flex-col gap-1 rounded-md bg-surface-base p-3">
            {checklistRow(
              newPassword.length >= PASSWORD_MIN_LENGTH && newPassword.length <= PASSWORD_MAX_LENGTH,
              t('accountSettings.password.rule.length', { min: PASSWORD_MIN_LENGTH, max: PASSWORD_MAX_LENGTH }),
            )}
            {checklistRow(!check.rejections.includes('blocked_term'), t('accountSettings.password.rule.notCommon'))}
            {checklistRow(!check.rejections.includes('contains_username'), t('accountSettings.password.rule.notUsername'))}
          </ul>
          {serverPasswordRejections.length > 0 ? (
            <p className="text-sm text-status-critical-tint-fg">
              {serverPasswordRejections.map((code) => t(`accountSettings.password.rule.serverRejected.${code}`)).join(' ')}
            </p>
          ) : null}
        </div>

        <PasswordField
          id="account-confirm-password"
          label={t('accountSettings.password.confirm')}
          autoComplete="new-password"
          value={confirmPassword}
          onChange={(e) => setConfirmPassword(e.target.value)}
          onBlur={() => setTouched((prev) => ({ ...prev, confirm: true }))}
          errorText={confirmMismatch ? t('accountSettings.password.rule.mismatch') : undefined}
          required
        />

        <Button
          className="self-start"
          onClick={submit}
          loading={mutation.isPending}
          loadingLabel={t('accountSettings.password.submitting')}
          disabled={!canSubmit}
        >
          {t('accountSettings.password.submit')}
        </Button>
      </Card>

      <AlertBanner variant="info" title={t('accountSettings.notBuilt.title')}>
        {t('accountSettings.notBuilt.body')}
      </AlertBanner>
    </div>
  );
}
